import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { FiArrowLeft, FiCopy, FiPlus, FiSearch, FiAlertTriangle } from 'react-icons/fi';
import { doDelete, get, post } from '../../../helpers/httpClient';
import { PropertyTypeIcon } from '../../../helpers/propertyTypeIcons';
import LayoutForm from '../../../helpers/LayoutForm';

const MAX_VISIBLE_CHIPS = 6;

function allColumns(layout) {
  return (layout.rows ?? []).flatMap((r) => r.columns ?? []);
}

function allComponents(layout) {
  return allColumns(layout).flatMap((c) => c.components ?? []);
}

// A miniature, true-to-proportion sketch of the layout: one strip per row, each column
// sized by its actual width%. Far more identifiable at a glance than a raw "3 cols" count.
function LayoutPreview({ layout }) {
  const rows = layout.rows ?? [];
  if (rows.length === 0) return null;
  return (
    <div className="gl-preview" aria-hidden="true">
      {rows.map((row, rIdx) => {
        const columns = row.columns ?? [];
        if (columns.length === 0) return null;
        return (
          <div key={rIdx} className="gl-preview-row">
            {columns.map((col, cIdx) => {
              const width = Number(col.width) || 0;
              return (
                <div
                  key={cIdx}
                  className="gl-preview-col"
                  style={{ flex: `1 1 ${Math.max(width, 4)}%` }}
                >
                  {width >= 12 ? `${Math.round(width)}%` : ''}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function PropertyChips({ layout }) {
  const props = allComponents(layout).filter((p) => (p.name || '').trim());
  if (props.length === 0) {
    return <span className="gl-chip gl-chip--muted">No properties yet</span>;
  }
  const visible = props.slice(0, MAX_VISIBLE_CHIPS);
  const overflow = props.length - visible.length;
  return (
    <>
      {visible.map((p, i) => (
        <span key={i} className={`gl-chip item-prop-type-badge--${p.type}`} title={`${p.name} (${p.type})`}>
          <PropertyTypeIcon type={p.type} size={12} />
          {p.name}
        </span>
      ))}
      {overflow > 0 && <span className="gl-chip gl-chip--muted">+{overflow}</span>}
    </>
  );
}

function Layouts({ website }) {
  const websiteId = website.id;

  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingLayout, setEditingLayout] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('name');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await get(`websites/${websiteId}/layouts`);
      setLayouts(resp?.layouts ?? []);
    } catch {
      /* httpClient shows toast */
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  useEffect(() => { load(); }, [load]);

  const visibleLayouts = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? layouts.filter((t) => {
          if ((t.name || '').toLowerCase().includes(q)) return true;
          // Searching by field name is how you actually look for a layout ("the one with a price"),
          // so property names are matched too, not just the layout's own name.
          return allComponents(t).some((p) => (p.name || '').toLowerCase().includes(q));
        })
      : [...layouts];
    return filtered.sort((a, b) => {
      if (sort === 'usage') return (b.usage_count ?? 0) - (a.usage_count ?? 0);
      if (sort === 'newest') return String(b.created_at ?? '').localeCompare(String(a.created_at ?? ''));
      return String(a.name ?? '').localeCompare(String(b.name ?? ''));
    });
  }, [layouts, query, sort]);

  const handleCreated = (layout) => {
    setLayouts((prev) => [...prev, layout]);
    setCreating(false);
  };

  const handleEdit = async (id) => {
    try {
      setOpeningId(id);
      const resp = await get(`websites/${websiteId}/layouts/${id}`);
      setEditingLayout(resp.layout);
    } catch {
      /* httpClient shows toast */
    } finally {
      setOpeningId(null);
    }
  };

  const handleUpdated = (layout) => {
    // The edit response has no usage data, so carry over what the list already knew rather
    // than letting the badge blank out until the next full reload.
    setLayouts((prev) => prev.map((t) => (t.id === layout.id
      ? { ...layout, usage_count: t.usage_count, used_by: t.used_by }
      : t)));
    setEditingLayout(null);
  };

  const handleDuplicate = async (layout) => {
    try {
      setDuplicatingId(layout.id);
      // Fetch the full record first: the list payload is enough to render a card, but a copy
      // has to carry the mobile template and every per-property setting across too.
      const resp = await get(`websites/${websiteId}/layouts/${layout.id}`);
      const full = resp.layout;
      const created = await post(`websites/${websiteId}/layouts`, {
        name: `${full.name} (copy)`,
        schema_type: full.schema_type ?? 'article',
        rows: full.rows ?? [],
        mobile: { components: full.mobile_template?.components ?? [] },
      });
      setLayouts((prev) => [...prev, { ...created.layout, usage_count: 0, used_by: [] }]);
    } catch {
      /* httpClient shows toast */
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleDelete = async (layout) => {
    try {
      setDeletingId(layout.id);
      await doDelete(`websites/${websiteId}/layouts/${layout.id}`);
      setLayouts((prev) => prev.filter((t) => t.id !== layout.id));
      setConfirmDelete(null);
    } catch {
      /* httpClient shows toast; backend refuses if the layout is still referenced */
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="text-center my-4"><Spinner animation="border" /></div>;
  }

  if (editingLayout) {
    return (
      <div>
        <div className="structure-header-row mb-3">
          <div className="gl-edit-heading">
            <button type="button" className="gl-back-btn" onClick={() => setEditingLayout(null)} aria-label="Back to layouts">
              <FiArrowLeft size={15} />
            </button>
            <div>
              <div className="gl-edit-crumb">Layouts</div>
              <h5 className="mb-0">{editingLayout.name}</h5>
            </div>
          </div>
        </div>
        <LayoutForm
          websiteId={websiteId}
          initial={editingLayout}
          onSaved={handleUpdated}
          onCancel={() => setEditingLayout(null)}
        />
      </div>
    );
  }

  if (creating) {
    return (
      <div>
        <div className="structure-header-row mb-3">
          <div className="gl-edit-heading">
            <button type="button" className="gl-back-btn" onClick={() => setCreating(false)} aria-label="Back to layouts">
              <FiArrowLeft size={15} />
            </button>
            <div>
              <div className="gl-edit-crumb">Layouts</div>
              <h5 className="mb-0">New layout</h5>
            </div>
          </div>
        </div>
        <LayoutForm
          websiteId={websiteId}
          onSaved={handleCreated}
          onCancel={() => setCreating(false)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="structure-header-row mb-3">
        <h5 className="mb-0">Layouts</h5>
        <Button size="sm" type="button" className="primary-button m-0" onClick={() => setCreating(true)}>
          <FiPlus size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />New layout
        </Button>
      </div>

      {layouts.length > 0 && (
        <div className="gl-toolbar">
          <span className="gl-search">
            <FiSearch size={13} className="gl-search-icon" />
            <input
              type="text"
              className="form-control form-control-sm"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by layout or field name"
              aria-label="Search layouts"
            />
          </span>
          <select
            className="form-control form-control-sm gl-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sort layouts"
          >
            <option value="name">Name A–Z</option>
            <option value="usage">Most used</option>
            <option value="newest">Newest first</option>
          </select>
        </div>
      )}

      {layouts.length === 0 && (
        <div className="gl-empty">
          <div className="gl-empty-title">Create your first layout</div>
          <p className="gl-empty-body">
            A layout defines the rows, columns and fields that catalog items and pages are built from —
            set it up once and every page using it stays consistent.
          </p>
          <Button size="sm" type="button" className="primary-button m-0" onClick={() => setCreating(true)}>
            <FiPlus size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />New layout
          </Button>
        </div>
      )}

      {layouts.length > 0 && visibleLayouts.length === 0 && (
        <div className="gl-empty">No layouts match “{query}”.</div>
      )}

      {visibleLayouts.map((t) => {
        const rowCount = (t.rows ?? []).length;
        const colCount = allColumns(t).length;
        const propCount = allComponents(t).length;
        const usage = t.usage_count ?? 0;
        return (
          <div key={t.id} className="lo-card">
            <div className="gl-card-head">
              <div className="gl-card-titles">
                <div className="gl-card-name">{t.name}</div>
                <div className="gl-card-sub">
                  {rowCount} row{rowCount !== 1 ? 's' : ''} · {colCount} column{colCount !== 1 ? 's' : ''} · {propCount} field{propCount !== 1 ? 's' : ''}
                </div>
              </div>
              <div className="gl-card-actions">
                <span
                  className={`gl-usage${usage === 0 ? ' gl-usage--unused' : ''}`}
                  title={usage > 0 ? (t.used_by ?? []).map((p) => p.title).join(', ') : 'Not used by any page'}
                >
                  {usage > 0 ? `${usage} page${usage !== 1 ? 's' : ''}` : 'Unused'}
                </span>
                <Button
                  size="sm"
                  type="button"
                  className="primary-button m-0"
                  onClick={() => handleEdit(t.id)}
                  disabled={openingId === t.id}
                >
                  {openingId === t.id ? <Spinner animation="border" size="sm" /> : 'Edit'}
                </Button>
                <button
                  type="button"
                  className="gl-icon-btn"
                  onClick={() => handleDuplicate(t)}
                  disabled={duplicatingId === t.id}
                  aria-label={`Duplicate layout ${t.name}`}
                  title="Duplicate"
                >
                  {duplicatingId === t.id ? <Spinner animation="border" size="sm" /> : <FiCopy size={14} />}
                </button>
                <button
                  type="button"
                  className="gl-delete-btn"
                  onClick={() => setConfirmDelete(t)}
                  aria-label={`Delete layout ${t.name}`}
                >
                  Delete
                </button>
              </div>
            </div>

            <LayoutPreview layout={t} />

            <div className="gl-card-chips">
              <PropertyChips layout={t} />
            </div>
          </div>
        );
      })}

      <Modal show={!!confirmDelete} onHide={() => setConfirmDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Delete “{confirmDelete?.name}”?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(confirmDelete?.usage_count ?? 0) > 0 ? (
            <>
              <div className="gl-modal-warning">
                <FiAlertTriangle size={15} />
                <span>
                  This layout is used by {confirmDelete.usage_count} page
                  {confirmDelete.usage_count !== 1 ? 's' : ''}. Switch {confirmDelete.usage_count !== 1 ? 'them' : 'it'} to
                  another layout first.
                </span>
              </div>
              <ul className="gl-modal-pages">
                {(confirmDelete?.used_by ?? []).map((p) => (
                  <li key={p.id}>{p.title}</li>
                ))}
              </ul>
            </>
          ) : (
            <p className="mb-0">
              This layout isn’t used by any page. Deleting it can’t be undone.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" className="primary-button" onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <button
            type="button"
            className="gl-danger-btn"
            onClick={() => handleDelete(confirmDelete)}
            disabled={(confirmDelete?.usage_count ?? 0) > 0 || deletingId === confirmDelete?.id}
          >
            {deletingId === confirmDelete?.id ? <Spinner animation="border" size="sm" /> : 'Delete layout'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Layouts;
